import { Component, inject, OnInit, signal } from '@angular/core';
import { CrudOperations } from '../../interfaces/crud_table';
import { Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { HeaderService } from '../../services/header.service';
import { ServiceService } from '../../services/service.service';
import {
  Column,
  DataTableComponent,
} from '../../components/data-table/data-table.component';
import { ButtonModule } from 'primeng/button';
import { MediaService } from '../../services/media.service';
import {
  Attributes,
  ServiceDataTable,
  Services,
} from '../../interfaces/services';
import { formatDateOrDefault } from '../../utils/date';
import { AddServiceComponent } from './add-service/add-service.component';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-services',
  standalone: true,
  imports: [DataTableComponent, AddServiceComponent, ButtonModule],
  templateUrl: './services.component.html',
})
export class ServicesComponent implements OnInit, CrudOperations<any> {
  private readonly _headerService = inject(HeaderService);
  private readonly _serviceService = inject(ServiceService);
  private readonly _mediaService = inject(MediaService);
  private readonly _messageService = inject(MessageService);
  private readonly _confirmDialog = inject(ConfirmationService);
  private readonly _router = inject(Router);

  protected services = signal<Services>({} as Services);
  protected servicesDataTable = signal<ServiceDataTable[]>([]);
  protected totalRecords = signal<number>(0);
  protected dialogVisible = false;
  protected loading = signal<boolean>(false);
  protected currentSearch = '';

  protected columns: Column = [
    { field: 'id', header: 'ID', sortable: true },
    { field: 'clientName', header: 'Cliente', sortable: true },
    { field: 'address', header: 'Dirección', sortable: true },
    { field: 'createAt', header: 'Fecha de creación', sortable: true },
    { field: 'finishAt', header: 'Fecha de finalización', sortable: true },
    { field: 'updatedAt', header: 'Última actualización', sortable: true },
    { field: 'status', header: 'Estado', sortable: true },
  ];

  protected filters: string[] = ['id', 'status', 'address', 'finishAt'];

  ngOnInit(): void {
    this._headerService.setHeader('Servicios', false, 'pi pi-hammer', 'bg-blue-100', 'text-blue-500');
    this.loadServices();
  }

  private loadServices(page: number = 1, pageSize: number = 10): void {
    this._serviceService.fetchServices(page, pageSize, this.currentSearch).subscribe({
      next: (services) => {
        this.services.set(services);
        this.totalRecords.set(services.meta.pagination.total);
        this.servicesDataTable.set(
          this.services()
            .data.map((service) => ({
              id: service.id,
              clientName: service.attributes.client?.data?.attributes?.name || 'Sin cliente',
              address: service.attributes.address,
              createAt: formatDateOrDefault(service.attributes.createdAt) || '',
              updatedAt:
                formatDateOrDefault(service.attributes.updatedAt) || '',
              finishAt: formatDateOrDefault(service.attributes.finishAt) || '',
              status: service.attributes.status || '',
            }))
            .sort((a, b) => b.id - a.id)
        );
      },
    });
  }

  onAdd(): void {
    this.dialogVisible = true;
  }
  onSearch(query: string): void {
    this.currentSearch = query;
    this.loadServices(1, 10);
  }
  onEdit(item: any): void {
    this._router.navigate(['services', item.id]);
  }
  onRemoveSelected(items: any[]): void {
    this._confirmDialog.confirm({
      message: '¿Está seguro que desea eliminar los servicios seleccionados?',
      header: 'Confirmación',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        const deleteRequests = items.map((item) =>
          this._serviceService.deleteService(item.id)
        );
        forkJoin(deleteRequests).subscribe({
          next: () => {
            this._messageService.add({
              severity: 'success',
              summary: 'Servicios eliminados',
              detail: 'Servicios eliminados correctamente',
            });
            this.loadServices();
          },
          error: () => {
            this._messageService.add({
              severity: 'error',
              summary: 'Error al eliminar servicios',
              detail: 'Ha ocurrido un error al eliminar los servicios',
            });
          },
        });
      },
    });
  }

  onPage(event: { page: number; rows: number }): void {
    const page = event.page + 1;
    const pageSize = event.rows;
    this.loadServices(page, pageSize);
  }

  onSave({
    serviceData,
    files,
  }: {
    serviceData: Attributes;
    files: File[];
  }): void {
    const createService = (fileIds: number[] = []) => {
      const postData = {
        ...serviceData,
        media: fileIds,
        client: serviceData.client,
      };

      if (!serviceData.startAt) {
        delete postData.startAt;
      }

      this._serviceService.addService(postData).subscribe({
        next: () => {
          this._messageService.add({
            severity: 'success',
            summary: 'Servicio creado',
            detail: 'Servicio creado correctamente',
          });
          this.loadServices();
          this.dialogVisible = false;
        },
        error: () => {
          this._messageService.add({
            severity: 'error',
            summary: 'Servicio no creado',
            detail: 'Ha ocurrido un error al crear el servicio',
          });
        },
      });
    };
    if (files && files.length > 0) {
      this._mediaService.uploadFiles(files).subscribe({
        next: (uploadResponse) => {
          const fileIds = uploadResponse.map((file: any) => file.id);
          createService(fileIds);
        },
        error: () => {
          this._messageService.add({
            severity: 'error',
            summary: 'Error al subir archivos',
            detail: 'Ha ocurrido un error al subir los archivos',
          });
        },
      });
    } else {
      createService();
    }
  }
}
